import { useEffect, useMemo, useState } from "react";
import { discussions, faqs, siteProfile, categories } from "@/mocks/discussion";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import HotFeed, { type OrderKey } from "./components/HotFeed";
import CategoryBoard from "./components/CategoryBoard";
import FeaturedToday from "./components/FeaturedToday";
import CommunityVoice from "./components/CommunityVoice";
import FaqSection from "./components/FaqSection";
import SubmitForm from "./components/SubmitForm";
import Footer from "./components/Footer";
import DiscussionDrawer from "./components/DiscussionDrawer";
import type { Discussion } from "./components/types";

function SeoJsonLd() {
  const canonical = typeof window !== "undefined" ? window.location.origin + "/" : "/";
  const schemas = useMemo(() => {
    const base = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: siteProfile.brand,
      description: siteProfile.desc,
      url: canonical,
    };
    const org = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteProfile.brand,
      description: siteProfile.desc,
      url: canonical,
      email: siteProfile.email,
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "热议广场实时话题",
      itemListElement: discussions.map((d, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: d.title,
      })),
    };
    return [org, base, faqSchema, itemList];
  }, [canonical]);

  return (
    <>
      {schemas.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }}
        />
      ))}
    </>
  );
}

export default function Home() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [order, setOrder] = useState<OrderKey>("hot");
  const [active, setActive] = useState<Discussion | null>(null);
  const [showTop, setShowTop] = useState(false);

  const featureTopic = discussions[0];

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToFeed = () => {
    document.getElementById("feed")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pickTag = (tag: string) => {
    setQuery(tag);
    scrollToFeed();
  };

  const pickCategory = (name: string) => {
    setCategory(category === name ? "" : name);
    scrollToFeed();
  };

  const openTopic = (t: Discussion) => setActive(t);

  return (
    <>
      <SeoJsonLd />

      {/* 面包屑（单页结构） */}
      <nav aria-label="面包屑" className="sr-only">
        <ol className="flex gap-1 text-xs">
          <li>首页</li>
          <li aria-hidden="true">/</li>
          <li>热议广场</li>
        </ol>
      </nav>

      <Navbar />
      <main>
        <Hero query={query} onSearch={setQuery} onPickTag={pickTag} />
        <HotFeed
          query={query}
          category={category}
          order={order}
          setOrder={setOrder}
          onOpen={openTopic}
          onReset={() => {
            setQuery("");
            setCategory("");
          }}
        />
        <CategoryBoard onSelect={pickCategory} />
        <FeaturedToday topic={featureTopic} onOpen={openTopic} />
        <CommunityVoice />
        <FaqSection />
        <SubmitForm />
      </main>
      <Footer />

      <DiscussionDrawer topic={active} onClose={() => setActive(null)} />

      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="anim-pop fixed bottom-6 right-6 z-40 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-white shadow-soft transition-colors hover:bg-primary-600"
          aria-label="返回顶部"
        >
          <i className="ri-arrow-up-line text-lg" aria-hidden="true"></i>
        </button>
      )}

      {/* 分类可见内容：链接到热议广场（供站内结构化语义，不产生死链） */}
      <div className="hidden">
        {categories.map((c) => (
          <a key={c.id} href="#feed" title={`${c.name}话题`}>
            {c.name}
          </a>
        ))}
      </div>
    </>
  );
}